"use client";

import React from "react";
import type { FieldErrors, UseFormRegister } from "react-hook-form";
import ErrorMessage from "@/src/shared/components/atoms/ErrorMessage";

interface IBookingMessageProps {
  // Form integration props
  register?: UseFormRegister<{ message?: string }>;
  error?: FieldErrors<{ message?: string }> | null;

  // Content props
  label?: string;
  placeholder?: string;
  iconSrc?: string;
  iconAlt?: string;

  // Behavior props
  rows?: number;
  onClick?: () => void;
  onChange?: (value: string) => void; // Add onChange prop

  // Styling props
  className?: string;

  // Accessibility props
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

const BookingMessage: React.FC<IBookingMessageProps> = ({
  register,
  error,
  label = "Ask me anything you want",
  placeholder = "Write Something...",
  iconSrc = "/icons/chats.png",
  iconAlt = "Message",
  rows = 3,
  onClick,
  onChange,
  className = "",
  ...ariaProps
}) => {
  const hasError = !!error?.message;
  const errorId = "message-error";

  return (
    <div className={`space-y-1 ${className}`}>
      {/* Message card */}
      <div
        className={` w-full rounded-xl border px-4 flex items-center gap-3 transition-colors
          ${
            hasError
              ? "border-red-500 bg-red-50 shadow-red-100"
              : "border-gray-300 bg-white shadow-sm hover:border-gray-400"
          }`}
      >
        {/* Icon */}
        <div className="grid place-items-center w-9 h-9 rounded-xl self-start pt-3">
          <img src={iconSrc} alt={iconAlt} className="w-5 h-5" />
        </div>

        {/* Input area */}
        <div className="flex-1 pt-3 min-w-0">
          <div
            className={`text-sm font-semibold mb-1
              ${hasError ? "text-danger" : "text-ink-gray"}`}
          >
            {label}
          </div>

          {/* Textarea */}
          <textarea
            onClick={onClick}
            name="message"
            rows={rows}
            placeholder={placeholder}
            {...register?.("message")}
            aria-invalid={hasError}
            aria-describedby={hasError ? errorId : undefined}
            {...ariaProps}
            className={`
              mt-0.5 w-full resize-none border-none p-0 bg-transparent outline-none
              text-sm font-medium
              ${
                hasError
                  ? "text-danger placeholder-red-400"
                  : "text-ink-black placeholder-neutral-400"
              }
            `}
          />
        </div>
      </div>
      <ErrorMessage message={error?.message?.message} />
    </div>
  );
};

export default BookingMessage;
